import React from 'react';
import { TemplateRenderProps } from './types';
import { FormFields, AddonsSelector, CouponSection, TrustAndSecurityBadge, SocialProofAndFomo } from './CheckoutCommonElements';
import { BillingCycleSelector, PlanFeatureList, TestimonialCard, PriceBreakdown } from './CheckoutSummaryElements';

export const BentoTemplate: React.FC<TemplateRenderProps> = ({
  plan,
  config,
  formData,
  onInputChange,
  billingCycle,
  onChangeCycle,
  selectedAddonIds,
  onToggleAddon,
  appliedCoupon,
  onApplyCoupon,
  onRemoveCoupon,
  onSubmitInfo,
  isCapturing,
  currencySymbol,
  addonsTotal,
  discountAmount,
  finalTotal,
}) => {
  return (
    <div
      className="min-h-full p-4 md:p-8 flex items-center justify-center"
      style={{
        backgroundColor: config.backgroundColor || '#f8fafc',
        color: config.textColor || '#0f172a',
        fontFamily: config.fontFamily || 'Inter, sans-serif',
      }}
    >
      <div className="w-full max-w-4xl grid grid-cols-1 md:grid-cols-12 gap-4">
        {/* Top Header Bento Card */}
        <div className="md:col-span-8 bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-200 dark:border-slate-800 shadow-sm flex items-center justify-between">
          <div className="flex items-center gap-3">
            {config.logoUrl && <img src={config.logoUrl} alt="Logo" className="h-6 object-contain" />}
            <div>
              <h2 className="text-base font-bold text-slate-900 dark:text-white leading-none">{plan.tier} Plan</h2>
              <p className="text-[11px] text-slate-500 mt-0.5">Selected Membership</p>
            </div>
          </div>
          <span className="text-xs font-black text-indigo-600 bg-indigo-50 dark:bg-indigo-950/50 px-2.5 py-1 rounded-full">
            Active Tier
          </span>
        </div>

        {/* Pricing Bento Card */}
        <div className="md:col-span-4 md:row-span-2 bg-slate-900 text-white rounded-2xl p-6 shadow-md flex flex-col justify-between space-y-6">
          <div className="space-y-4">
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Order Totals</span>
            <div>
              <p className="text-3xl font-black text-indigo-400">{currencySymbol}{finalTotal.toFixed(2)}</p>
              <p className="text-xs text-slate-400 mt-0.5">Billed {billingCycle}</p>
            </div>
            <BillingCycleSelector billingCycle={billingCycle} onChangeCycle={onChangeCycle} config={config} />
            <PriceBreakdown plan={plan} billingCycle={billingCycle} addonsTotal={addonsTotal} discountAmount={discountAmount} finalTotal={finalTotal} currencySymbol={currencySymbol} />
          </div>
          {config.showTestimonial && <TestimonialCard testimonial={config.testimonial} />}
        </div>

        {/* Form Bento Card */}
        <div className="md:col-span-8 bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200 dark:border-slate-800 shadow-sm space-y-5">
          <div>
            <h1 className="text-lg font-bold text-slate-900 dark:text-white">{config.headerMain || 'Billing Information'}</h1>
            <p className="text-xs text-slate-500">{config.headerSub || 'Please provide your account and payment details.'}</p>
          </div>

          <SocialProofAndFomo config={config} />

          <form onSubmit={onSubmitInfo} className="space-y-4">
            <FormFields config={config} formData={formData} onInputChange={onInputChange} />
            <AddonsSelector addons={config.addons} selectedAddonIds={selectedAddonIds} onToggleAddon={onToggleAddon} currency={plan.currency} />
            <CouponSection config={config} appliedCoupon={appliedCoupon} onApplyCoupon={onApplyCoupon} onRemoveCoupon={onRemoveCoupon} />

            <button
              type="submit"
              disabled={isCapturing}
              className="w-full py-3.5 text-xs font-black uppercase tracking-wider text-white shadow transition-all hover:opacity-90 disabled:opacity-50"
              style={{
                backgroundColor: config.primaryColor || '#4f46e5',
                borderRadius: config.buttonRadius || '10px',
              }}
            >
              {isCapturing ? 'Verifying...' : config.buttonText || 'Confirm & Checkout'}
            </button>
          </form>

          <TrustAndSecurityBadge config={config} />
        </div>
      </div>
    </div>
  );
};
